<template>
  <q-card class="q-mb-md bg-grey-1 event-card" flat bordered>
    <div class="row no-wrap event-card__wrapper">
      <div class="col-xs-12 col-sm-4 event-card__img q-py-sm">
        <q-img :src="event.thumbnail" class="full-height" />
      </div>
      <div class="col-xs-12 col-sm-7 event-card__detail">
        <div class="q-pb-md">
          <div class="text-h6 q-mb-md text-weight-bold">
            {{ event.name }}
          </div>
          <div class="text-subtitle2">
            <span>{{ $t('events.eventStart') }}:</span>
            {{ formatDate(event.startDate) }}
          </div>

          <div class="text-subtitle2">
            <span>{{ $t('events.location') }}:</span>
            {{ locationLabel }}
          </div>

          <div class="text-subtitle2" v-if="event.eventStatus">
            <span>{{ $t('events.eventStatus') }}:</span>
            {{ $t(`events.${event.eventStatus}`) }}
          </div>
        </div>

        <div class="card-buttons">
          <div class="card-buttons__primary">
            <template v-for="action in cardActions" :key="action.name">
              <q-btn
                :color="action.color || 'primary'"
                :outline="action.outline"
                :icon="action.icon"
                class="card-buttons__action"
                :label="$t(action.label)"
                @click="handleAction(action.name)"
              />
            </template>
          </div>

          <div class="card-buttons__secondary">
            <q-btn
              v-if="event.eventStatus && event.eventStatus !== 'unverified'"
              icon="edit"
              color="secondary"
              round
              size="sm"
              class="card-buttons__edit"
              @click="handleEdit"
            >
              <q-tooltip>{{ $t('events.editTooltip') }}</q-tooltip>
            </q-btn>

            <q-btn
              v-if="event.eventStatus && event.eventStatus === 'unverified'"
              icon="delete_forever"
              color="white"
              round
              flat
              size="sm"
              class="card-buttons__delete"
              :loading="isDeleting"
              @click="handleDelete"
            >
              <q-tooltip>{{ $t('events.deleteTooltip') }}</q-tooltip>
              <template v-slot:loading>
                <q-spinner color="negative" size="1em" />
              </template>
            </q-btn>
          </div>
        </div>
      </div>
    </div>
  </q-card>
</template>

<script>
import { date } from 'quasar';
import userRoleMixin from '@/mixins/userRoleMixin.js';

export default {
  name: 'EventCard',
  mixins: [userRoleMixin],
  props: {
    event: {
      type: Object,
      required: true,
    },
    isDeleting: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    locationLabel() {
      const cityLabel = this.getCityLabel(this.event.city?.id);
      const countryLabel = this.getCountryLabel(this.event.country?.id);

      const parts = [cityLabel, countryLabel].filter(Boolean);

      if (parts.length) {
        return parts.join(', ');
      }

      return `${this.event.eventCity} - ${this.$t('events.locationInVerification')}`;
    },
    cardActions() {
      if (!this.event.eventStatus && this.roleConfig.cardActions) {
        return this.roleConfig.cardActions;
      }
      return [];
    },
  },
  methods: {
    formatDate(dateString) {
      return date.formatDate(dateString, 'YYYY/MM/DD');
    },
    handleAction(actionName) {
      this.$emit('card-action', {
        actionName,
        eventId: this.event.id,
      });
    },
    handleEdit() {
      this.$emit('edit', this.event.id);
    },
    handleDelete() {
      this.$emit('delete', this.event.id);
    },
  },
};
</script>

<style lang="scss" scoped>
.event-card {
  max-width: 850px;
  margin: 0 auto;

  &__wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-content: center;
    gap: 14px;
  }

  &__img {
    margin: 0 auto;
    width: 100%;
    height: 250px;
  }

  &__detail {
    display: flex;
    flex-direction: column;
    padding: 0.75rem;
    overflow: hidden;
  }
}

.card-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;
  &__primary {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    flex: 1;
  }

  &__action {
    min-width: 66px;
  }

  &__icon-action {
    min-width: 36px;
    height: 36px;
  }

  &__secondary {
    display: flex;
    gap: 0.25rem;
    align-items: center;
  }

  &__edit,
  &__delete {
    background-color: $primary;
    min-width: 50px;
    height: 36px;
  }
}

.text-subtitle2 {
  margin-top: 5px;

  & span {
    font-weight: 600;
    margin-right: 6px;
  }
}

@media (min-width: $breakpoint-xs-max) {
  .event-card {
    &__wrapper {
      flex-direction: row;
    }

    &__img {
      margin-left: 10px;
      height: 200px;
      max-width: 200px;
      flex-shrink: inherit;
    }

    &__detail {
      margin: 0;
      justify-content: space-between;
      align-items: flex-start;
      flex-grow: 1;
    }
  }

  .card-buttons {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    &__primary {
      flex: 1;
    }

    &__secondary {
      justify-content: flex-end;
    }
  }
}
</style>
